import { useContext, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';

import { mdiInformation } from '@mdi/js';
import Icon from '@mdi/react';
import iziToast from 'izitoast';
import { Check, ClipboardCopy, Rocket, UploadIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import { toast } from 'sonner';
import Permission from 'src/components/shared/Permission';
import { Checkbox } from 'src/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { AppAgentContext } from '../../../../context/ContextProvider';
import { deployAgentServiceV2, upsertAgentServiceV2 } from '../../../../services/AgentService';
import { appActions, State } from '../../../../store';
import { ModalType } from '../../../../types/Modal';
import { copyToClipboard, generateJsBotFile, grantPermission } from '../../../../utils';
import { CheckboxComponent, SelectComponent } from '../../../../utils/UtilsComponent';
import ButtonComponent from '../../../shared/ButtonComponent';
import { Input } from '../../../ui/input';
import { Switch } from '../../../ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import { valueAttachmentDefault } from '../../bot-script/BotScript';
import { AgentSetupContext, extraConfigDefault } from '../AgentSetupComponent';
import AnimatedLoadingScreen from './AnimatedLoadingScreen';
import Select from 'react-select';

export interface OptionsUploadScript {
    assessor: number | string;
    withWhatsApp: boolean;
    defaultCountry?: string;
    withMicrophone: boolean;
    withAssessor: boolean;
    withAttachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean; csv: boolean };
    withInitialMessage: boolean;
}

const metadataProps = ['city', 'country', 'ip', 'timezone', 'region'];

const AgentDeployComponent = () => {
    const { t: agent } = useTranslate('agent');
    // const { t: common } = useTranslate('common');
    const [copied, setCopied] = useState(false);

    const {
        state: {
            session: { user_id, ai_agent_config, origin, domainName, permissions, countries },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();
    const { getValues, setValue } = useContext(AgentSetupContext);
    const [loading, setLoading] = useState(false);
    const [deployAgent, setDeployAgent] = useState({
        deployed_chat: false,
        deployed_voice: false,
        deployed_whatsapp: false,
    });
    const [activeAttachment, setActiveAttachment] = useState(
        Object.values(
            getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment || valueAttachmentDefault
        ).some((v) => v)
    );

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const generateBot = (
        assessorId: string | number,
        whatsapp: boolean,
        microphone: boolean,
        withAssessor: boolean,
        attachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean },
        initialMessage: boolean,
        defaultCountry: string = '+57'
    ) => {
        dispatch(appActions.closeModal(ModalType.DYNAMIC));
        const agentForm = getValues();
        if (id) {
            generateJsBotFile(
                id,
                agentForm.name.split(' ').join('_'),
                ai_agent_config?.folders?.scripts ?? '',
                assessorId,
                origin.concat('/www/', domainName, '/ai-agent/'),
                whatsapp,
                microphone,
                withAssessor,
                attachment,
                initialMessage,
                countries,
                defaultCountry,
                (state) => {
                    switch (state) {
                        case State.LOADING:
                            iziToast.info({
                                message: `${agent('generating_script')}...`,
                                position: 'bottomLeft',
                            });
                            break;
                        case State.SUCCESS:
                            iziToast.success({
                                message: `${agent('script_generated_successfully')}.`,
                                position: 'bottomLeft',
                            });
                            break;
                        case State.ERROR:
                            iziToast.warning({
                                message: `${agent('script_generation_failed')}`,
                                position: 'bottomLeft',
                            });
                            break;
                    }
                },
                'v2'
            ).then();
        }
    };

    function updatedConfigBot(values: OptionsUploadScript) {
        let submittedData;

        if (!getValues().look_and_feel_chat?.extra_config) {
            submittedData = {
                ...getValues(),
                look_and_feel_chat: {
                    ...getValues().look_and_feel_chat,
                    extra_config: JSON.stringify({
                        ...extraConfigDefault,
                        config_bot: values,
                    }),
                },
            };
        } else {
            submittedData = {
                ...getValues(),
                look_and_feel_chat: {
                    ...getValues().look_and_feel_chat,
                    extra_config: JSON.stringify({
                        ...getValues().look_and_feel_chat.extra_config,
                        config_bot: values,
                    }),
                },
            };
        }

        if (id && id !== 'create') {
            submittedData.id = parseInt(id);
        }
        upsertAgentServiceV2(submittedData).then();
    }

    const deploy = async () => {
        setLoading(true);
        const meta_data: { [key: string]: string } = {};

        try {
            const responseMeta = await fetch('https://ipapi.co/json/');
            const data = await responseMeta.json();
            if (data && Object.keys(data).length > 0) {
                metadataProps.forEach((key) => {
                    if (data[key]) {
                        meta_data[key] = data[key];
                    }
                });
            }
        } catch (e) {}

        const response = await deployAgentServiceV2({
            agent_id: id as string,
            user: user_id.toString(),
            meta_data: JSON.stringify(meta_data),
            ...deployAgent,
        });

        if (response?.success) {
            toast.success(agent('agent_deployed_successfully'));
        } else {
            toast.error(response?.message || agent('agent_deployed_failed'));
        }

        setLoading(false);
    };

    const getAgentName = () => {
        const agentForm = getValues();
        return agentForm.name.split(' ').join('_');
    };

    const countriesOptions = useMemo(() => {
        return countries.map((country) => ({
            value: country.code,
            label: (
                <div className="flex items-center gap-2">
                    <span>
                        <img
                            src={`https://flagcdn.com/w40/${country.country.toLowerCase()}.png`}
                            width="40"
                            alt={country.name}
                        />
                    </span>
                    <span>{country.code}</span>
                </div>
            ),
        }));
    }, [countries]);

    return loading ? (
        <AnimatedLoadingScreen message={agent('deploying_agent')} />
    ) : (
        <>
            <Card className="bg-gradient-to-br from-purple-100 to-pink-100 shadow-xl mb-3">
                <CardHeader className="flex  md:flex-row md:items-center justify-between">
                    <CardTitle className="text-2xl font-bold text-purple-800">{agent('upload_script')}</CardTitle>
                    <div className="flex items-center gap-2">
                        <Permission permission={AI_AGENT_V2_EDIT}>
                            <ButtonComponent
                                label={agent('upload_script')}
                                icon={<UploadIcon />}
                                className="secondary-gradient-button"
                                disabled={
                                    !getValues().look_and_feel_chat?.extra_config?.config_bot ||
                                    !getValues().look_and_feel_chat?.extra_config?.config_bot?.assessor ||
                                    (Object.values(
                                        getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment || {
                                            ...valueAttachmentDefault,
                                        }
                                    ).every((v) => !v) &&
                                        activeAttachment) ||
                                    (getValues().look_and_feel_chat?.extra_config?.config_bot?.withWhatsApp &&
                                        !getValues().look_and_feel_chat?.extra_config?.config_bot?.default_country)
                                }
                                onClick={() => {
                                    const state = getValues().look_and_feel_chat?.extra_config?.config_bot || {
                                        assessor: undefined,
                                        withWhatsApp: false,
                                        withMicrophone: false,
                                        withAssessor: true,
                                        withAttachment: valueAttachmentDefault,
                                        withInitialMessage: true,
                                    };
                                    updatedConfigBot(state);
                                    generateBot(
                                        state.assessor,
                                        state.withWhatsApp,
                                        state.withMicrophone,
                                        state.withAssessor,
                                        state.withAttachment,
                                        state.withInitialMessage,
                                        state.defaultCountry || '+57'
                                    );
                                }}
                            />
                        </Permission>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="px-3">
                        <div className="relative">
                            <SelectComponent
                                label={''}
                                options={ai_agent_config?.assesors || []}
                                disabled={id !== 'create' && !allowEdit}
                                value={getValues().look_and_feel_chat?.extra_config?.config_bot?.assessor}
                                onChange={(option) => {
                                    setValue('look_and_feel_chat.extra_config.config_bot.assessor', option);
                                }}
                            />
                        </div>
                        <div className="">
                            <ActiveOptionAgent
                                label="WhatsApp"
                                description={agent('active_whatsapp_bot')}
                                value={getValues().look_and_feel_chat?.extra_config?.config_bot?.withWhatsApp}
                                setValue={(val) =>
                                    setValue('look_and_feel_chat.extra_config.config_bot.withWhatsApp', val)
                                }
                                disabled={id !== 'create' && !allowEdit}
                            />
                            {getValues().look_and_feel_chat?.extra_config?.config_bot?.withWhatsApp && (
                                <Select
                                    className="mt-2"
                                    options={countriesOptions}
                                    isDisabled={id !== 'create' && !allowEdit}
                                    value={
                                        countriesOptions.find(
                                            (co) =>
                                                co.value ===
                                                getValues().look_and_feel_chat?.extra_config?.config_bot
                                                    ?.default_country
                                        ) || null
                                    }
                                    onChange={(option) => {
                                        setValue(
                                            'look_and_feel_chat.extra_config.config_bot.default_country',
                                            option?.value || ''
                                        );
                                    }}
                                    placeholder={agent('select_default_country')}
                                />
                            )}
                            <ActiveOptionAgent
                                label={agent('microphone')}
                                description={agent('active_microphone_bot')}
                                value={getValues().look_and_feel_chat?.extra_config?.config_bot?.withMicrophone}
                                setValue={(val) =>
                                    setValue('look_and_feel_chat.extra_config.config_bot.withMicrophone', val)
                                }
                                disabled={id !== 'create' && !allowEdit}
                            />
                            <ActiveOptionAgent
                                label={agent('assessor')}
                                description={agent('active_assessor_bot')}
                                value={getValues().look_and_feel_chat?.extra_config?.config_bot?.withAssessor}
                                setValue={(val) =>
                                    setValue('look_and_feel_chat.extra_config.config_bot.withAssessor', val)
                                }
                                disabled={id !== 'create' && !allowEdit}
                            />
                            <ActiveOptionAgent
                                label={agent('attachment')}
                                description={agent('active_attachment_bot')}
                                value={activeAttachment}
                                setValue={(val) => {
                                    if (!val) {
                                        setValue(
                                            'look_and_feel_chat.extra_config.config_bot.withAttachment',
                                            valueAttachmentDefault
                                        );
                                    }
                                    setActiveAttachment(val);
                                }}
                                disabled={id !== 'create' && !allowEdit}
                            />
                            {activeAttachment && (
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="images"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.image',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.image
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.image
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="images" className="text-sm">
                                            Images
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="pdf-file"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.pdf',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.pdf
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.pdf
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="pdf-file" className="text-sm">
                                            PDF
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="excel-file"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.excel',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.excel
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.excel
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="excel-file" className="text-sm">
                                            Excel
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="csv-file"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.csv',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.csv
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.csv
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="csv-file" className="text-sm">
                                            CSV
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="text-file"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.text',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.text
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.text
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="text-file" className="text-sm">
                                            Text
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            id="word-file"
                                            onClick={() =>
                                                setValue(
                                                    'look_and_feel_chat.extra_config.config_bot.withAttachment.word',
                                                    !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                        ?.withAttachment?.word
                                                )
                                            }
                                            checked={
                                                getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.word
                                            }
                                            disabled={id !== 'create' && !allowEdit}
                                        />
                                        <label htmlFor="word-file" className="text-sm">
                                            Word
                                        </label>
                                    </div>
                                </div>
                            )}
                            <ActiveOptionAgent
                                label={agent('initial_message')}
                                description={agent('active_initial_message_bot')}
                                value={getValues().look_and_feel_chat?.extra_config?.config_bot?.withInitialMessage}
                                setValue={(val) =>
                                    setValue('look_and_feel_chat.extra_config.config_bot.withInitialMessage', val)
                                }
                                disabled={id !== 'create' && !allowEdit}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-100 to-pink-100 shadow-xl">
                <CardHeader className="flex  md:flex-row md:items-center justify-between">
                    <CardTitle className="text-2xl font-bold text-purple-800">{agent('deploy')}</CardTitle>

                    <Permission permission={AI_AGENT_V2_EDIT}>
                        <div className="flex items-center gap-2">
                            <ButtonComponent
                                label={agent('deploy_agent')}
                                icon={<Rocket />}
                                className="primary-gradient-button"
                                onClick={deploy}
                            />
                        </div>
                    </Permission>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                        <CheckboxComponent
                            label={agent('deploy_to_chat')}
                            classNameInput="data-[state=checked]:!bg-purple-700 !border-purple-700"
                            classNameLabel={'form-input-label'}
                            value={deployAgent.deployed_chat}
                            onChange={(value) => {
                                setDeployAgent((prevState) => ({
                                    ...prevState,
                                    deployed_chat: value,
                                }));
                            }}
                            disabled={id !== 'create' && !allowEdit}
                        />

                        {deployAgent.deployed_chat && (
                            <TooltipProvider>
                                <div className="flex flex-col space-y-2  w-[600px] px-4 relative">
                                    <div className="flex space-x-2">
                                        <Input
                                            type="text"
                                            value={`${origin}/www/${domainName}/ai-agent/scripts/${getAgentName()}.js`}
                                            disabled
                                            className="pr-10 w-full"
                                            aria-label="Disabled input field"
                                        />
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={() => {
                                                        copyToClipboard(
                                                            `${origin}/www/${domainName}/ai-agent/scripts/${getAgentName()}.js`
                                                        );
                                                        setCopied(true);
                                                        setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
                                                    }}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-gray-400 rounded-full p-1"
                                                    aria-label="Copy to clipboard"
                                                >
                                                    {copied ? (
                                                        <Check className="h-4 w-4" />
                                                    ) : (
                                                        <ClipboardCopy className="h-4 w-4" />
                                                    )}
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{copied ? 'Copied!' : 'Copy to clipboard'}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </div>
                            </TooltipProvider>
                        )}
                    </div>

                    <CheckboxComponent
                        label={agent('deploy_to_voice')}
                        classNameInput="data-[state=checked]:!bg-purple-700 !border-purple-700"
                        classNameLabel={'form-input-label'}
                        value={deployAgent.deployed_voice}
                        onChange={(value) => {
                            setDeployAgent((prevState) => ({
                                ...prevState,
                                deployed_voice: value,
                            }));
                        }}
                        disabled={id !== 'create' && !allowEdit}
                    />

                    <CheckboxComponent
                        label={agent('deploy_to_whatsapp')}
                        classNameInput="data-[state=checked]:!bg-purple-700 !border-purple-700"
                        classNameLabel={'form-input-label'}
                        value={deployAgent.deployed_whatsapp}
                        onChange={(value) => {
                            setDeployAgent((prevState) => ({
                                ...prevState,
                                deployed_whatsapp: value,
                            }));
                        }}
                        disabled={id !== 'create' && !allowEdit}
                    />
                </CardContent>
            </Card>
        </>
    );
};

export default AgentDeployComponent;

export function ActiveOptionAgent({
    value,
    setValue,
    label,
    description,
    disabled,
}: {
    value: boolean;
    setValue: (val: boolean) => void;
    label: string;
    description: string;
    disabled?: boolean;
}) {
    return (
        <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2">
                <label htmlFor="">{label}</label>
                <Popover>
                    <PopoverTrigger>
                        <div className="rounded-full bg-black">
                            <Icon path={mdiInformation} size={0.7} className="text-white fill-white" />
                        </div>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div>
                            <p className="text-sm text-center">{description}</p>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
            <Switch
                className={`data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200 scale-90`}
                checked={value}
                disabled={disabled}
                onClick={() => {
                    setValue(!value);
                }}
            />
        </div>
    );
}
